import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {
    buildSiteUpgradeAnalysis,
    priorityMeta,
    renderSiteUpgradeAnalysis,
    summarizeSiteUpgradeAnalysis,
} from "../../helpers/site_upgrade_analysis";
import {renderUpgradeStrip, UPGRADE_NEUTRAL_ICON} from "../../helpers/upgrade_analysis_ui";

// 64-bit ids now arrive from CBOR as exact decimal strings, so compare them as
// strings first. The numeric fallback covers ids read from the URL and older
// payloads whose low digits were already rounded.
function idsMatch(a, b) {
    if (a === null || a === undefined || b === null || b === undefined) {
        return false;
    }
    if (String(a) === String(b)) {
        return true;
    }
    const na = Number(a);
    const nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

export class ShaperSiteUpgradeAnalysis extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastPayload = null;
    }

    title() {
        return "Upgrade Analysis";
    }

    channelRequests() {
        return ["SiteUpgradeCandidates"];
    }

    buildContainer() {
        const base = super.buildContainer();
        this.container = base;
        base.classList.add("lqos-upgrade-card");
        const title = base.querySelector(".dashbox-title");
        if (title) title.classList.add("d-none");
        // Only shown when the payload has circuits recommended for upgrade.
        base.style.display = "none";
        const body = document.createElement("div");
        body.id = `siteUpgradeAnalysis_${this.slotNumber}`;
        body.setAttribute("aria-busy", "true");
        this.contentId = body.id;
        base.appendChild(body);
        return base;
    }

    setVisible(visible) {
        if (this.container) {
            this.container.style.display = visible ? "" : "none";
        }
    }

    onLoadStart(channels) {
        if (!Array.isArray(channels) || !channels.includes("SiteUpgradeCandidates")) {
            return;
        }
        this.setVisible(false);
        const body = document.getElementById(this.contentId);
        if (body) {
            body.innerHTML = "";
            body.setAttribute("aria-busy", "true");
        }
    }

    onScopeChange() {
        this.lastPayload = null;
        this.onLoadStart(["SiteUpgradeCandidates"]);
    }

    canExplain() {
        // Only participate when there are circuits recommended for upgrade.
        if (this.lastPayload === null) return false;
        const analysis = buildSiteUpgradeAnalysis(this.lastPayload, globalThis.window?.siteId);
        return (Number(analysis.siteRow?.candidate_count) || analysis.circuitRows.length) > 0;
    }

    isExplainReady() {
        return this.lastPayload !== null;
    }

    explain() {
        if (!this.lastPayload) return null;
        return {
            dashlet_name: this.title(),
            description: summarizeSiteUpgradeAnalysis(this.lastPayload, globalThis.window?.siteId),
        };
    }

    currentRequestId() {
        const requestId = globalThis.window?.siteUpgradeCandidatesRequestId;
        const numeric = Number(requestId);
        return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
    }

    payloadMatchesCurrentScope(payload) {
        const currentSiteId = globalThis.window?.siteId;
        if (currentSiteId !== undefined && payload?.site_id !== undefined) {
            if (!idsMatch(payload.site_id, currentSiteId)) {
                return false;
            }
        }

        const currentRequestId = this.currentRequestId();
        if (currentRequestId === null) {
            return true;
        }
        const payloadRequestId = Number(payload?.request_id);
        return Number.isInteger(payloadRequestId) && payloadRequestId === currentRequestId;
    }

    onMessage(msg) {
        if (msg?.Data?.target !== "site_upgrade_candidates") return;
        const payload = msg.Data.blob || {};
        if (!this.payloadMatchesCurrentScope(payload)) return;

        const body = document.getElementById(this.contentId);
        if (!body) return;

        this.lastPayload = payload;
        const siteId = globalThis.window?.siteId;
        const analysis = buildSiteUpgradeAnalysis(payload, siteId);
        const siteRow = analysis.siteRow;
        const candidateCount = Number(siteRow?.candidate_count) || analysis.circuitRows.length;
        const siteName = siteRow?.site_name || `Site ${siteId ?? ""}`.trim();

        if (candidateCount <= 0) {
            if (analysis.capacityCircuitCount <= 0) {
                // No circuit at this site has a usable plan limit, so "no
                // candidates" is an unavailable analysis, not a clean result.
                this.setVisible(true);
                body.setAttribute("aria-busy", "false");
                renderUpgradeStrip(body, {
                    icon: "fa-gauge-high",
                    badgeLabel: "Plan limits unavailable",
                    headline: `No usable plan limits at ${siteName}`,
                    detail: "Cannot judge upgrade pressure",
                    muted: true,
                    modalTitle: `Upgrade Analysis — ${siteName}`,
                    buildBody: () => {
                        const holder = document.createElement("div");
                        renderSiteUpgradeAnalysis(holder, payload, siteId, { neutralBadge: true });
                        return holder.innerHTML;
                    },
                });
                this.rendered = true;
                return;
            }
            this.setVisible(false);
            body.innerHTML = "";
            body.setAttribute("aria-busy", "false");
            return;
        }

        const meta = priorityMeta(siteRow);
        const urgent = Number(siteRow?.urgent_count) || 0;
        const watch = Number(siteRow?.watch_count) || 0;
        const parts = [];
        if (urgent > 0) parts.push(`${urgent} urgent`);
        if (watch > 0) parts.push(`${watch} watch`);

        this.setVisible(true);
        body.setAttribute("aria-busy", "false");
        renderUpgradeStrip(body, {
            icon: UPGRADE_NEUTRAL_ICON,
            badgeLabel: meta.title,
            headline: `${candidateCount} candidate ${candidateCount === 1 ? "circuit" : "circuits"} at ${siteName}`,
            detail: parts.join(" / "),
            modalTitle: `Upgrade Analysis — ${siteName}`,
            buildBody: () => {
                const holder = document.createElement("div");
                renderSiteUpgradeAnalysis(holder, payload, siteId, { neutralBadge: true });
                return holder.innerHTML;
            },
        });
        this.rendered = true;
    }
}
